import { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { sendContactForm, resetContactFormState } from "../../redux/contactSlice";
import FormIcon from "../../Assets/Svg/Form/FormIcon.svg";
import "./Form.css";

export default function FormWeb() {
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.contact);

  const formRef = useRef();
  const [captcha, setCaptcha] = useState("");
  const [userCaptchaInput, setUserCaptchaInput] = useState("");
  const [isCaptchaOpen, setIsCaptchaOpen] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [nameError, setNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [subjectError, setSubjectError] = useState("");
  const [messageError, setMessageError] = useState("");

  useEffect(() => {
    generateCaptcha();
    dispatch(resetContactFormState());
  }, [dispatch]);

  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10);
    const num2 = Math.floor(Math.random() * 10);
    setCaptcha(`${num1} + ${num2}`);
  };

  const validateName = (e) => {
    const value = e.target.value.trim();
    if (value.length < 3) setNameError("Name must be at least 3 characters.");
    else setNameError("");
  };

  const validateEmail = (e) => {
    const value = e.target.value.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) setEmailError("Please enter a valid email address.");
    else setEmailError("");
  };

  const validatePhone = (e) => {
    const value = e.target.value.trim();
    if (value.length < 11) setPhoneError("Enter a valid phone number.");
    else setPhoneError("");
  };

  const validateSubject = (e) => {
    const value = e.target.value.trim();
    if (value.length < 3) setSubjectError("Subject must be at least 3 characters.");
    else setSubjectError("");
  };

  const validateMessage = (e) => {
    const value = e.target.value.trim();
    if (value.length < 3) setMessageError("Message must be at least 3 characters.");
    else setMessageError("");
  };

  const validateInputs = (formData) => {
    let isValid = true;

    const requiredFields = [
      { field: "user_name", setError: setNameError, error: "Name is required." },
      { field: "user_email", setError: setEmailError, error: "Email is required.", regex: /^[^\s@]+@[^\s@]+\.[^\s@]+$/ },
      { field: "user_phone", setError: setPhoneError, error: "Phone is required." },
      { field: "user_subject", setError: setSubjectError, error: "Subject is required." },
      { field: "user_message", setError: setMessageError, error: "Message is required." },
    ];

    for (const { field, setError, error, regex } of requiredFields) {
      const value = formData.get(field)?.trim();
      if (!value || (regex && !regex.test(value))) {
        setError(error);
        isValid = false;
      } else {
        setError("");
      }
    }

    return isValid;
  };

  const sendEmailAfterVerification = (e) => {
    e.preventDefault();
    const form = formRef.current;
    const formData = new FormData(form);
    if (!validateInputs(formData)) return;
    setIsCaptchaOpen(true);
  };

  const verifyCaptchaAndSendEmail = async () => {
    const captchaValues = captcha.split(" + ").map(Number);
    const correctCaptchaAnswer = captchaValues[0] + captchaValues[1];

    if (parseInt(userCaptchaInput) !== correctCaptchaAnswer) {
      alert("Captcha verification failed. Please try again.");
      return;
    }

    try {
      const form = formRef.current;
      const formData = new FormData(form);
      const payload = {
        name: formData.get("user_name"),
        email: formData.get("user_email"),
        phone: formData.get("user_phone"),
        city: formData.get("user_city"),
        message: formData.get("user_message"),
        subject: formData.get("user_subject"),
      };

      await dispatch(sendContactForm(payload)).unwrap();

      setIsSuccess(true);
      setUserCaptchaInput("");
      setCaptcha("");
      form.reset();
      generateCaptcha();
    } catch (err) {
      console.error("Form submission failed:", err);
      if (typeof err === "string") alert(err);
      else if (typeof err === "object" && err.errors) {
        const allErrors = Object.values(err.errors).flat().join("\n");
        alert(allErrors);
      } else {
        alert("Something went wrong. Please try again.");
      }
    }
  };

  const enforceWordLimit = (event, maxWords) => {
    const textarea = event.target;
    const words = textarea.value.trim().split(/\s+/);
    if (words.length > maxWords) {
      textarea.value = words.slice(0, maxWords).join(" ");
      alert(`You can only enter up to ${maxWords} words.`);
    }
  };

  return (
    <>
      <form ref={formRef} onSubmit={sendEmailAfterVerification}>
        <div className="ContactUsBig">
          <div className="FormWebContainer">
            <div className="FormWTC">
              <div className="FormWP1">
                <InputBox label="Name" name="user_name" required onBlur={validateName} error={nameError} placeholder="Name" />
                <InputBox label="Email" name="user_email" required onBlur={validateEmail} error={emailError} placeholder="example@gmail.com" />
                <InputBox label="Phone Number" name="user_phone" required onBlur={validatePhone} error={phoneError} placeholder="+20" />
                <InputBox label="City" name="user_city" placeholder="Your city" />
              </div>
              <div className="FormWP1">
                <InputBox label="Subject" name="user_subject" required onBlur={validateSubject} error={subjectError} placeholder="Message subject" />
                <InputBox
                  label="Message"
                  name="user_message"
                  textarea
                  required
                  onBlur={validateMessage}
                  onInput={(e) => enforceWordLimit(e, 1000)}
                  error={messageError}
                  placeholder="Your message"
                />
              </div>
            </div>

            <div className="FormWSubmit">
              <button className="FromWsubmitB TFut16PxPCUC" type="submit" disabled={loading}>
                {loading ? "Sending..." : "Submit Inquiry"} <img src={FormIcon} alt="" />
              </button>
            </div>
          </div>
        </div>
      </form>

      {isCaptchaOpen && (
        <CaptchaModal
          captcha={captcha}
          userCaptchaInput={userCaptchaInput}
          setUserCaptchaInput={setUserCaptchaInput}
          onCancel={() => {
            setIsCaptchaOpen(false);
            setUserCaptchaInput("");
            generateCaptcha();
          }}
          onVerify={verifyCaptchaAndSendEmail}
          isLoading={loading}
          isSuccess={isSuccess}
        />
      )}
    </>
  );
}

function InputBox({ label, name, required, placeholder, onBlur, onInput, error, textarea }) {
  return (
    <div className="FiledBoxContainer">
      <div className="InputFieldBox">
        <div className="IFBT">
          <div className="IFBTN TFut24PxPC">{label}</div>
          {required && <div className="IFBTR TFut16PxPCUC">(required)</div>}
        </div>
        <div className="InputMob">
          <label className="InputLabelMob">
            {textarea ? (
              <textarea className="IFBI TFut20PxPC" name={name} placeholder={placeholder} onBlur={onBlur} onInput={onInput} />
            ) : (
              <input className="IFBI TFut20PxPC" type="text" name={name} placeholder={placeholder} onBlur={onBlur} />
            )}
          </label>
        </div>
      </div>
      {error && <div className={`error-message${textarea ? "_Last" : ""} TF16PxSC`}>{error}</div>}
    </div>
  );
}

function CaptchaModal({ captcha, userCaptchaInput, setUserCaptchaInput, onVerify, onCancel, isLoading, isSuccess }) {
  return (
    <div className="captchaModal">
      <div className="captchaModalContent">
        {isLoading ? (
          <div className="CPLoading TFut16PxPCUC">Loading...</div>
        ) : isSuccess ? (
          <div className="CaptchaSubmit">
            <div className="CPMessage TFut16PxPCUC">Thanks, your message was sent. We'll get back to you soon.</div>
            <button onClick={onCancel} className="CaptchaSubB TFut16PxPCUC">
              Close
            </button>
          </div>
        ) : (
          <div className="CaptchaQAContainer">
            <div className="CaptchaQ">
              <div className="CaptchaQP1 TGLo32PxPC">Human Verification</div>
              <div className="CaptchaQP2 TFut16PxPCUC">What is {captcha}?</div>
            </div>
            <div className="CaptchaAA">
              <input
                type="text"
                className="CaptchaAnswerF TFut16PxPCUC"
                placeholder="_"
                value={userCaptchaInput}
                onChange={(e) => setUserCaptchaInput(e.target.value)}
              />
              <div className="CaptchaBB">
                <button onClick={onCancel} className="CaptchaC TFut16PxPCUC">
                  Cancel
                </button>
                <button onClick={onVerify} className="CaptchaV TFut16PxPCUC">
                  Verify
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
